import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { clsx } from 'clsx';

import type { QueryBuilderChangeEvent } from '../../types/QueryBuilder';

import { QueryBuilderContext } from './context';
import { useQueryBuilderSetup } from './hooks/useQueryBuilderSetup';
import type { ClipboardDialect } from './utils';

import styles from './QueryBuilder.module.css';

/** Trailing delay before a commit is copied to the URL — coalesces live keystroke writes. */
const QUERY_PARAM_WRITE_DEBOUNCE_MS = 150;

function isTextEntry(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

interface QueryBuilderProps {
  /** Controlled query string. When provided, external changes sync into internal state. */
  value?: string;
  /** Initial query string for uncontrolled usage. */
  defaultValue?: string;
  /** Called whenever the committed query changes. */
  onChange?: (event: QueryBuilderChangeEvent) => void;
  /** URL param the bar is controlled by: read on every render, written back on each commit. */
  queryParamKey?: string;
  /** Params deleted whenever the query changes, e.g. `['offset']` to return to the first page. */
  resetParams?: string[];
  /** Additional class name(s) merged with the built-in root class via clsx. */
  className?: string;
  /**
   * Clipboard serialization dialect. `lezer` (default) copies `field:=:value`.
   * `antlr` copies observability search syntax (`field=value`). Paste always
   * accepts either form.
   */
  clipboardDialect?: ClipboardDialect;
  /** Override pasted-text normalization. Defaults to Lezer-or-ANTLR detection. */
  parseClipboard?: (text: string) => string;
  children: React.ReactNode;
}

type QueryBuilderBaseProps = Omit<QueryBuilderProps, 'queryParamKey' | 'resetParams'> & {
  onFocusCapture?: React.FocusEventHandler<HTMLDivElement>;
  onBlurCapture?: React.FocusEventHandler<HTMLDivElement>;
};

function QueryBuilderBase({
  value,
  defaultValue,
  onChange,
  className,
  children,
  clipboardDialect,
  parseClipboard,
  onFocusCapture,
  onBlurCapture,
}: QueryBuilderBaseProps) {
  const { contextValue, wrapperRef } = useQueryBuilderSetup({
    value,
    defaultValue,
    onChange,
    clipboardDialect,
    parseClipboard,
  });

  return (
    <QueryBuilderContext.Provider value={contextValue}>
      <div
        ref={wrapperRef}
        role="presentation"
        className={clsx(styles.root, className)}
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
        onKeyDown={(event) => {
          // Enter in a token input implicitly submits an enclosing form, e.g. the alert and
          // adaptive trigger dialogs the bar is embedded in. Buttons keep their activation.
          if (event.key === 'Enter' && isTextEntry(event.target)) {
            event.preventDefault();
          }
        }}
      >
        {children}
      </div>
    </QueryBuilderContext.Provider>
  );
}

function QueryParamSyncedQueryBuilder({
  queryParamKey,
  resetParams = [],
  onChange,
  className,
  children,
  clipboardDialect,
  parseClipboard,
}: Omit<QueryBuilderBaseProps, 'value' | 'defaultValue' | 'onFocusCapture' | 'onBlurCapture'> & {
  queryParamKey: string;
  resetParams?: string[];
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(queryParamKey) ?? '';
  // Bar state updates immediately; the URL lags by the debounce so keystroke bursts
  // cannot rewind the bar with a stale echo or spam list fetches.
  const [value, setValue] = useState(urlValue);
  const lastWrittenRef = useRef(urlValue);
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Delayed writes must call this render's setSearchParams; react-router closes over that render's params.
  const setSearchParamsRef = useLatestRef(setSearchParams);
  const isTypingRef = useRef(false);

  useEffect(() => {
    if (urlValue === lastWrittenRef.current) {
      return;
    }
    // A debounced write landing after a later keystroke would otherwise rewind what is being typed.
    if (isTypingRef.current) {
      return;
    }
    if (writeTimerRef.current !== null) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }
    lastWrittenRef.current = urlValue;
    setValue(urlValue);
  }, [urlValue]);

  useEffect(
    () => () => {
      if (writeTimerRef.current !== null) {
        clearTimeout(writeTimerRef.current);
      }
    },
    [],
  );

  function handleChange(event: QueryBuilderChangeEvent) {
    lastWrittenRef.current = event.query;
    setValue(event.query);
    if (writeTimerRef.current !== null) {
      clearTimeout(writeTimerRef.current);
      writeTimerRef.current = null;
    }

    const writeToUrl = () => {
      writeTimerRef.current = null;
      const query = lastWrittenRef.current;
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (query) {
            next.set(queryParamKey, query);
          } else {
            next.delete(queryParamKey);
          }
          for (const param of resetParams) {
            next.delete(param);
          }
          return next;
        },
        { replace: true },
      );
    };

    // Empty commits flush immediately so clear-filters cannot race a pending keystroke write.
    if (!event.query) {
      writeToUrl();
    } else {
      writeTimerRef.current = setTimeout(writeToUrl, QUERY_PARAM_WRITE_DEBOUNCE_MS);
    }
    onChange?.(event);
  }

  return (
    <QueryBuilderBase
      value={value}
      onChange={handleChange}
      className={className}
      clipboardDialect={clipboardDialect}
      parseClipboard={parseClipboard}
      onFocusCapture={(event) => {
        isTypingRef.current = isTextEntry(event.target);
      }}
      onBlurCapture={(event) => {
        const next = event.relatedTarget;
        isTypingRef.current = next instanceof Node && event.currentTarget.contains(next) && isTextEntry(next);
      }}
    >
      {children}
    </QueryBuilderBase>
  );
}

export function QueryBuilder({ queryParamKey, resetParams, value, defaultValue, ...props }: QueryBuilderProps) {
  if (queryParamKey) {
    return <QueryParamSyncedQueryBuilder queryParamKey={queryParamKey} resetParams={resetParams} {...props} />;
  }
  return <QueryBuilderBase value={value} defaultValue={defaultValue} {...props} />;
}

QueryBuilder.displayName = 'QueryBuilder';
